import { liquidGlassRowShadowSx, liquidGlassSurfaceFillSx, translateTemplateText, useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Collapse, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import type { TFunction } from 'i18next';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AchievementCondition, QuestMetricType } from '../../client/generated/graphql';
import { ListAchievement } from '../../client/types';
import { calculateAchievementProgress, getConditionIcon } from '../../helpers/achievement';
import { useCachedQuestsWithFallback } from '../../hooks/caching/quest';
import { CommonIcon } from '../common/CommonIcon';
import { BorderLinearProgress } from './BorderLinearProgress';
import { ConditionPercentageSkeleton } from './skeleton/ConditionPercentageSkeleton';
import { ConditionQuestsSkeleton } from './skeleton/ConditionQuestsSkeleton';
import { ConditionTextSkeleton } from './skeleton/ConditionTextSkeleton';

interface AchievementConditionsDisplayProps {
  achievement: ListAchievement;
  expanded: boolean;
  setExpanded: (open: boolean) => void;
}

const getConditionLabel = (metricType: QuestMetricType, t: TFunction): string => {
  switch (metricType) {
    case QuestMetricType.Experience:
      return 'XP';
    case QuestMetricType.Completion:
      return t('achievement.complete');
    case QuestMetricType.Time:
      return t('achievement.time');
    case QuestMetricType.Repeating:
      return t('achievement.repeat');
    default:
      return t('achievement.unknown');
  }
};

const getConditionSuffix = (metricType: QuestMetricType, t: TFunction): string => {
    switch (metricType) {
      case QuestMetricType.Experience:
        return 'XP';
      case QuestMetricType.Completion:
        return t('achievement.completed');
      case QuestMetricType.Time:
        return t('achievement.hours');
      case QuestMetricType.Repeating:
        return t('achievement.times');
      default:
        return '';
    }
  };

const getConditionText = (condition: AchievementCondition, completed: number, t: TFunction): string => {
  switch (condition.metricType) {
    case QuestMetricType.Experience:
      return t('achievement.goalXp', { count: condition.target });
    case QuestMetricType.Completion:
      return t('achievement.completeAll');
    case QuestMetricType.Time:
      return t('achievement.progressHours', { done: completed, total: condition.target });
    case QuestMetricType.Repeating:
      return t('achievement.progressRepeated', { done: completed, total: condition.target });
    default:
      return t('achievement.unknownCondition');
  }
};

const sortQuestsByMetric = (
  questIds: string[],
  questMetrics: Record<string, number> | undefined,
  metricType: QuestMetricType
): string[] => {
  if (!questMetrics) return questIds;

  return [...questIds].sort((a, b) => {
    if (metricType === QuestMetricType.Completion) {
      // Sort uncompleted quests first
      const aCompleted = questMetrics[a] && questMetrics[a] > 0;
      const bCompleted = questMetrics[b] && questMetrics[b] > 0;

      if (aCompleted === bCompleted) return 0;
      return aCompleted ? 1 : -1;
    } else {
      // Sort by metric value from largest to lowest
      const aMetric = questMetrics[a] || 0;
      const bMetric = questMetrics[b] || 0;
      return bMetric - aMetric;
    }
  });
};

export const AchievementConditionsDisplay: FC<AchievementConditionsDisplayProps> = ({
  achievement,
  expanded,
  setExpanded
}) => {
  const { t } = useTranslation();
  const condition = achievement.achievementConditions[0];
  const { quests, loading } = useCachedQuestsWithFallback(condition?.questIds);
  const { completed, percentage, questMetrics } = calculateAchievementProgress(achievement, quests, true);

  const conditionSuffix = getConditionSuffix(condition.metricType, t);
  const conditionLabel = getConditionLabel(condition.metricType, t);
  const conditionIcon = getConditionIcon(condition.metricType);
  const conditionText = getConditionText(condition, completed, t);

  const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick } = useLiquidGlassPress({
    preventDefault: true,
    onActivate: () => {
      hapticFeedback.impactOccurred.ifAvailable('light');
      setExpanded(!expanded);
    },
  });

  return (
    <Box sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden', ...liquidGlassRowShadowSx }}>
      <Box sx={{ borderRadius: 'inherit', ...liquidGlassSurfaceFillSx }}>
        <Box
          component="button"
          type="button"
          aria-expanded={expanded}
          onClick={onClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          sx={{
            display: 'block',
            width: '100%',
            m: 0,
            px: '14px',
            py: '12px',
            border: 'none',
            background: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            bgcolor: pressed ? 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)' : 'transparent',
            transition: 'background-color 0.12s ease',
          }}
        >
          <Stack direction="row" gap="12px" alignItems="center">
            <CommonIcon
              icon={conditionIcon}
              type="filled"
              size={30}
              color={'var(--tg-theme-button-color)'}
            />
            <Stack direction="column" gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Text weight="2" style={{ color: 'var(--tg-theme-text-color)' }}>{conditionLabel}</Text>
              {loading ? (
                <ConditionTextSkeleton />
              ) : (
                <Text style={{ fontSize: '12px', color: 'var(--tg-theme-hint-color)' }}>
                  {conditionText}
                </Text>
              )}
            </Stack>
            <Stack direction="row" gap={1} alignItems="center" sx={{ flexShrink: 0 }}>
              {loading ? (
                <ConditionPercentageSkeleton />
              ) : (
                <Stack direction="column" gap={0.5} alignItems="flex-end">
                    <Text style={{ fontSize: '12px', color: 'var(--tg-theme-hint-color)' }}>
                         {percentage}%
                    </Text>
                    <BorderLinearProgress
                         variant="determinate"
                         value={percentage}
                         sx={{ height: '10px', width: '80px' }}
                     />
                </Stack>
              )}
              <CommonIcon
                icon={expanded ? 'expand_less' : 'expand_more'}
                type="filled"
                size={20}
                color="var(--tg-theme-hint-color)"
              />
            </Stack>
          </Stack>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              borderTop: '1px solid color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)',
              px: '14px',
              py: '12px',
            }}
          >
            {loading ? (
              <ConditionQuestsSkeleton />
            ) : condition.questIds && condition.questIds.length > 0 && (
              <Stack maxHeight="100px" gap={1} sx={{ overflow: 'scroll' }}>
                {sortQuestsByMetric(condition.questIds, questMetrics, condition.metricType)
                  .map(questId => {
                    const quest = quests.find(q => q.id === questId);
                    if (!quest) return null;

                    return (
                       <Stack key={questId} direction="row" gap={0.5} alignItems="center" justifyContent="space-between">
                           <Text style={{ fontSize: '12px', color: 'var(--tg-theme-text-color)' }}>{translateTemplateText(t, quest.templateId, 'name', quest.name)}</Text>
                           {questMetrics ?
                                condition.metricType === QuestMetricType.Completion ? (
                                        <CommonIcon
                                            icon={conditionIcon}
                                            type="filled"
                                            size={20}
                                            color={questMetrics[questId] && questMetrics[questId] > 0 ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-hint-color)'}
                                        />
                                ) : (
                                    <Text style={{ fontSize: '12px', color: 'var(--tg-theme-hint-color)' }}>
                                        {questMetrics[questId] || 0} {conditionSuffix}
                                    </Text>
                                )
                            : (<></>)}
                       </Stack>
                    );
                })}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};
